import { Label } from "flowbite-react";

function money(v) {
  if (v === null || v === undefined || v === "") return "—";
  return `$${Number(v).toLocaleString()}`;
}

function percent(v) {
  if (v === null || v === undefined || v === "") return "—";
  return `${(Number(v) * 100).toFixed(1)}%`;
}

function number(v) {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString();
}

function getValueTone(valueRatio) {
  if (!valueRatio) return null;
  if (valueRatio < 0.25) return "text-green-600 dark:text-green-400";
  if (valueRatio > 1) return "text-amber-600 dark:text-amber-400";
  return "text-gray-900 dark:text-white";
}

export default function PropertyCensusInsights({ snapshot, property }) {
  if (!snapshot) return null;

  const owner = Number(snapshot.owner_occupied_units || 0);
  const renter = Number(snapshot.renter_occupied_units || 0);
  const vacant = Number(snapshot.vacant_units || 0);

  const totalUnits = owner + renter + vacant;

  const ownerRatio = Number(snapshot.owner_occupied_ratio || 0);
  const renterRatio = owner + renter > 0 ? renter / (owner + renter) : null;
  const vacancyRate = Number(snapshot.vacancy_rate || 0);

  const medianIncome = Number(snapshot.median_household_income || 0);
  const medianValue = Number(snapshot.median_home_value || 0);
  const marketValue = Number(property?.total_value || 0);

  const valueRatio =
    marketValue && medianValue ? marketValue / medianValue : null;

  function getNeighborhoodAnalysis() {
    const insights = [];

    if (ownerRatio > 0.8) {
      insights.push(
        "Owner-dominated neighborhood, which usually suggests a more stable residential area.",
      );
    }

    if (ownerRatio < 0.5) {
      insights.push(
        "Rental-heavy area, which may indicate stronger investor presence.",
      );
    }

    if (vacancyRate > 0.2) {
      insights.push(
        "High vacancy rate. This can suggest seasonal homes, weak demand, or local economic softness.",
      );
    }

    if (vacancyRate < 0.08) {
      insights.push(
        "Low vacancy rate, which usually indicates healthier housing demand.",
      );
    }

    if (valueRatio && valueRatio < 0.25) {
      insights.push(
        "This property is priced far below the neighborhood median home value.",
      );
    }

    if (valueRatio && valueRatio > 1) {
      insights.push(
        "This property is above the neighborhood median home value.",
      );
    }

    if (medianIncome >= 75000) {
      insights.push(
        "Household income is relatively strong for a local residential market.",
      );
    } else if (medianIncome > 0 && medianIncome < 45000) {
      insights.push(
        "Household income is on the lower side, which may limit resale or rent potential depending on the area.",
      );
    }

    return insights;
  }

  const insights = getNeighborhoodAnalysis();

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 space-y-5">
      <div className="space-y-1">
        <div className="text-sm font-semibold text-gray-900 dark:text-white">
          Neighborhood Market Analysis
        </div>

        {snapshot.geo_name && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {snapshot.geo_name}
          </div>
        )}
      </div>

      {/* Market Context */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Market Context
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-800/40">
            <Label>Median Household Income</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {money(snapshot.median_household_income)}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Estimated neighborhood household income level
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 bg-gray-50 dark:bg-gray-800/40">
            <Label>Median Home Value</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {money(snapshot.median_home_value)}
            </div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Estimated neighborhood housing value benchmark
            </div>
          </div>
        </div>
      </div>

      {/* Occupancy Breakdown */}
      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Occupancy Breakdown
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3">
            <Label>Total Housing Units</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {number(totalUnits)}
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3">
            <Label>Vacant Units</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {number(vacant)}
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Vacancy Rate:{" "}
              <span className="font-semibold">{percent(vacancyRate)}</span>
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3">
            <Label>Owner Occupied</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {number(owner)}
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Owner Occupancy Rate:{" "}
              <span className="font-semibold">{percent(ownerRatio)}</span>
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 dark:border-gray-800 p-3 md:col-span-3">
            <Label>Rental Units</Label>
            <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
              {number(renter)}
            </div>
            <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              Rental Occupancy Rate:{" "}
              <span className="font-semibold">{percent(renterRatio)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">
            Neighborhood Insights
          </div>

          <ul className="list-disc ml-5 space-y-1 text-sm text-gray-600 dark:text-gray-300">
            {insights.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
